import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { usePage } from "contexts/PageContext";
import { useKeyboardList } from "hooks/useKeyboardList";

import { filterItems } from "../navFilter";
import { MOD_KEY_LABEL } from "../navShortcuts";
import { registerSearchBox } from "../searchBoxes";
import { NavItem, useNavItems } from "../useNavItems";

import "./PageSearch.scss";

interface PageSearchProps {
  /** The menu pane holds the search while it is open: this box keeps its place, unseen. */
  away?: boolean;
}

/**
 * Global page search in the navbar. Combobox over the same
 * permission-filtered page set the Sidebar shows.
 */
function PageSearch({ away = false }: PageSearchProps) {
  const { navigate } = usePage();
  const items = useNavItems();

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const results = useMemo(() => filterItems(items, query), [items, query]);
  const listOpen = open && query.trim() !== "";
  // The list renders in a portal at the input's position, so a page's own
  // stacked layers (sticky heads, tab strips) can never draw over it.
  const [anchor, setAnchor] = useState<DOMRect | null>(null);

  const close = (): void => {
    setQuery("");
    setOpen(false);
    inputRef.current?.blur();
  };

  const select = (item: NavItem | undefined): void => {
    if (!item) return;
    if (item.url) window.open(item.url, "_blank")?.focus();
    else navigate(item.action);
    close();
  };

  const kb = useKeyboardList({
    itemCount: listOpen ? results.length : 0,
    onSubmit: (i) => select(results[i]),
    onClear: close,
    resetSignal: `${open}::${query}`,
  });

  useLayoutEffect(() => {
    if (!listOpen) return;
    const place = (): void =>
      setAnchor(containerRef.current?.getBoundingClientRect() ?? null);
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [listOpen]);

  useLayoutEffect(() => {
    const box = containerRef.current;
    const input = inputRef.current;
    if (!box || !input) return;
    registerSearchBox("nav", { box, input });
    return () => registerSearchBox("nav", null);
  }, []);

  useEffect(() => {
    if (!away) return;
    setQuery("");
    setOpen(false);
  }, [away]);

  const listRef = kb.listRef;
  useEffect(() => {
    const onClick = (e: MouseEvent): void => {
      const target = e.target as Node;
      if (
        !containerRef.current?.contains(target) &&
        !listRef.current?.contains(target)
      )
        setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [listRef]);

  return (
    <div
      className={`wb-page-search${away ? " is-away" : ""}`}
      ref={containerRef}
      aria-hidden={away || undefined}
    >
      <i className="bi bi-search wb-page-search__icon" aria-hidden />
      <input
        ref={inputRef}
        className="wb-page-search__input"
        type="text"
        value={query}
        placeholder="Search pages…"
        tabIndex={away ? -1 : undefined}
        autoComplete="off"
        aria-label="Search pages"
        role="combobox"
        aria-expanded={listOpen}
        aria-controls="wb-page-search-list"
        aria-activedescendant={
          listOpen && results[kb.activeIndex]
            ? `wb-page-search-opt-${kb.activeIndex}`
            : undefined
        }
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={kb.onInputKeyDown}
      />
      {query === "" ? (
        <kbd className="wb-page-search__kbd" aria-hidden>
          {MOD_KEY_LABEL}
        </kbd>
      ) : null}
      {listOpen &&
        anchor &&
        createPortal(
          <div
            className="wb-page-search__list"
            id="wb-page-search-list"
            role="listbox"
            aria-label="Pages"
            ref={kb.listRef}
            style={{
              top: anchor.bottom + 4,
              left: anchor.left,
              width: Math.min(560, anchor.width),
            }}
          >
            {results.length === 0 && (
              <div className="wb-page-search__empty">No matches</div>
            )}
            {results.map((item, i) => (
              <button
                key={item.id}
                type="button"
                id={`wb-page-search-opt-${i}`}
                className={`wb-page-search__opt${
                  kb.isActive(i) ? " is-active" : ""
                }`}
                role="option"
                aria-selected={kb.isActive(i)}
                data-kb-index={i}
                onMouseEnter={() => kb.setActiveIndex(i)}
                onClick={() => select(item)}
              >
                <span className="wb-page-search__label">{item.label}</span>
                <span className="wb-page-search__section">{item.section}</span>
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}

export default PageSearch;
